import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * The form controls inside a node's config body. These used to be native
 * elements wrapped in hand-written chrome with hardcoded colours and an
 * `isDark` prop threaded down to every leaf. They are the house primitives
 * now, so a node's config looks like the rest of the product and follows the
 * theme without being told about it.
 */

/** The sequence inspector's field label, so a node's config reads like a step's. */
export const FIELD_LABEL = 'text-[12px] font-medium text-muted-foreground';

/**
 * One labelled control. A div rather than a label element: half these fields
 * hold a radiogroup, and wrapping one in a label makes clicking the label
 * pick the first option.
 */
export function ConfigField({
  label,
  action,
  children,
}: {
  label: string;
  /** Optional control on the right of the label row. */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className='space-y-1.5'>
      <div className='flex items-center justify-between gap-2'>
        <span className={FIELD_LABEL}>{label}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Groups a run of fields under a heading, the way the sequence settings do. */
export function ConfigSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className='space-y-4 border-t border-gray-100 pt-4 dark:border-white/[0.06]'>
      <h4 className='text-xs font-semibold text-foreground'>{title}</h4>
      {children}
    </section>
  );
}

export function ConfigInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Input
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className='h-9 text-sm'
    />
  );
}

export function ConfigTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <Textarea
      value={value}
      rows={rows}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className='resize-y text-sm'
    />
  );
}

export function ConfigSelect({
  value,
  options,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
  onOpenChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  /** Shown while nothing is chosen. Radix rejects an option with an empty
   *  value, so an unset select says so here rather than through a blank row. */
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Select
      value={value || undefined}
      onValueChange={onChange}
      onOpenChange={onOpenChange}
      disabled={disabled}
    >
      <SelectTrigger aria-label={ariaLabel} className='h-9 text-sm'>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className='max-h-[300px]'>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ConfigToggle({
  label,
  checked,
  onChange,
  state,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  /** A short muted state beside the switch ("From CRM settings"). */
  state?: string;
  disabled?: boolean;
}) {
  return (
    <div className='flex items-center justify-between gap-4 rounded-lg border border-gray-100 px-3 py-2.5 dark:border-white/[0.06]'>
      <span className='min-w-0 text-sm font-medium text-foreground'>
        {label}
      </span>
      {state && (
        <span className='ml-auto shrink-0 text-[12px] text-muted-foreground'>
          {state}
        </span>
      )}
      <Switch
        aria-label={label}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className='shrink-0'
      />
    </div>
  );
}

/**
 * A row of mutually exclusive choices (a radiogroup): the picked one tinted
 * blue, the selected-state colour.
 */
export function Segmented({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: { value: string; label: string; disabled?: boolean }[];
  onChange: (v: string) => void;
  /** The group's accessible name, when no visible label names it. */
  label?: string;
}) {
  return (
    <div
      role='radiogroup'
      aria-label={label}
      className='flex rounded-lg border border-gray-200 bg-gray-50/50 p-0.5 dark:border-white/[0.06] dark:bg-white/[0.02]'
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type='button'
            onClick={() => onChange(opt.value)}
            role='radio'
            aria-checked={active}
            disabled={opt.disabled}
            className={cn(
              'flex h-[30px] flex-1 items-center justify-center rounded-md border border-transparent px-2.5 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
              active
                ? 'border-[#3E8BFF]/25 bg-[#3E8BFF]/5 text-[#3E8BFF] shadow-xs dark:bg-[#3E8BFF]/10'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
